import type { User } from "@ferrivia/api-contract";
import { useTracker } from "@ferrivia/auth-web";
import { Button } from "@ferrivia/ui";
import { useTranslation } from "@ferrivia/i18n";
import { NavLink } from "react-router";
import { NAV_DESTINATIONS } from "../../nav";
import { initials } from "../../user";
import s from "./NavRail.module.css";

export interface NavRailProps {
  user: User;
  onSignOut: () => void;
}

function itemClass({ isActive }: { isActive: boolean }) {
  return isActive ? `${s.item} ${s.itemActive}` : s.item;
}

export function NavRail({ user, onSignOut }: NavRailProps) {
  const { t } = useTranslation("clinic");
  const tracker = useTracker();

  function signOut() {
    tracker.track("clinic.signed_out", { surface: "nav_rail" });
    onSignOut();
  }

  const labels: Record<string, string> = {
    "nav.queue": t("nav.queue", "Queue"),
    "nav.customers": t("nav.customers", "Customers"),
    "nav.tasks": t("nav.tasks", "Tasks"),
  };

  return (
    <nav className={s.root} aria-label={t("nav.sectionsLabel", "Sections")}>
      <div className={s.brandmark}>
        <span className={s.dot} aria-hidden="true">
          F
        </span>
        <span className={s.brandName}>Ferrivia</span>
      </div>

      {NAV_DESTINATIONS.map(({ path, labelKey, icon: Icon }) => (
        <NavLink key={path} to={path} className={itemClass}>
          <span className={s.icon} aria-hidden="true">
            <Icon size={20} strokeWidth={1.75} />
          </span>
          <span className={s.label}>{labels[labelKey]}</span>
        </NavLink>
      ))}

      <div className={s.spacer} />

      <div className={s.user}>
        <span className={s.avatar} aria-hidden="true">
          {initials(user)}
        </span>
        <div className={s.who}>
          <span className={s.email}>{user.email ?? t("navRail.noEmail", "No email")}</span>
          <span className={s.role}>{user.role}</span>
        </div>
      </div>
      <Button variant="ghost" className={s.signOut} onClick={signOut}>
        {t("navRail.signOut", "Sign out")}
      </Button>
    </nav>
  );
}
